import ApiErrorBanner from '@/components/ApiErrorBanner';
import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import ReasonForChange from '@/components/Shared/ReasonForChange';
import Button from '@/components/UI/Button';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative';
import type { MetadataFieldBuilderReturn } from '@/components/UI/FormFields/FormField.utils';
import { mapMetadataSubmissions, metadataFieldBuilder } from '@/components/UI/FormFields/FormField.utils';
import Timepoint from '@/components/UI/FormFields/Timepoint';
import FormRender from '@/components/UI/FormRender';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement, SampleMeasurementKey } from '@/model/Sample.model';
import type { GlossaryItem } from '@/model/Glosary.model';
import { useRequest } from '@/support/Hooks/request';
import { parseDuration } from '@/utils/DurationDsl';
import { DateUtils } from '@/utils/Date.utils';
import { ErrorMessage } from '@hookform/error-message';
import { serialize as durationSerialize } from 'tinyduration';
import type { AxiosResponse } from 'axios';
import type React from 'react';
//@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { Controller, ControllerRenderProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { DetailField } from './DetailField';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const plannedSamplesEnabledSelector = createSelector(
  [featuresSelector],
  (features): boolean => features?.planned_samples ?? false
);

interface InitialSample {
  sample_id?: string;
  date?: string;
  timepoint?: string;
  metadata: unknown;
  details: Array<SampleMeasurement>;
  comments?: string;
  reason_for_change?: string;
}

const initialiseSample = (sampleType: GlossaryItem, sample?: Sample): InitialSample => {
  const typeDetails = (sampleType?.options?.details ?? [])
    .filter((detailKey): detailKey is SampleMeasurementKey =>
      ['weight', 'width', 'length', 'depth', 'volume'].includes(detailKey)
    )
    .map<SampleMeasurement>((detailKey) => ({
      key: detailKey,
      unit: (samplesConfigObj?.[detailKey]?.units?.[0] ?? '') as SampleMeasurement['unit'],
      value: '' as SampleMeasurement['value'],
    }));

  if (_notNil(sample)) {
    const sampleDetailsMap = new Map<SampleMeasurement['key'], SampleMeasurement>(
      (sample?.details ?? []).map((detail) => [detail.key, detail])
    );

    return {
      sample_id: sample?.sample_id,
      details: typeDetails.map((detail) =>
        sampleDetailsMap.has(detail.key) ? (sampleDetailsMap.get(detail.key) as SampleMeasurement) : detail
      ),
      metadata: sample?.metadata,
      comments: sample?.comments,
      date: sample?.date ?? '',
      timepoint: _isNotBlank(sample?.timepoint)
        ? DateUtils.renderDuration(sample.timepoint, {
            format: 'short',
            zeros: true,
            defaultResponse: sample.timepoint,
          })
        : undefined,
    };
  }

  return {
    metadata: [],
    details: typeDetails,
    reason_for_change: '',
  };
};

const sampleMetadataFields = (
  sampleMetas: Array<MetadataFieldWithValue>,
  sample: InitialSample
): Array<MetadataFieldBuilderReturn> =>
  sampleMetas.map((meta: MetadataFieldWithValue) => metadataFieldBuilder(meta, sample));

interface SampleFormProps {
  animalId: ID;
  collectedAt: string;
  allowNaming: boolean;
  sample?: Sample;
  sampleType: GlossaryItem;
  sampleMetadataGlossary: Array<MetadataFieldWithValue>;
  handleAfterSave: (response: AxiosResponse) => void;
  saveButtonText: string;
  includeDatetimeField: boolean;
  formContainerRef: React.RefObject<HTMLFormElement>;
}

export const SampleForm: React.FC<SampleFormProps> = ({
  animalId,
  collectedAt,
  allowNaming = false,
  sample,
  sampleType,
  sampleMetadataGlossary,
  handleAfterSave,
  saveButtonText = 'Save',
  includeDatetimeField = false,
  formContainerRef,
}) => {
  const plannedSamplesEnabled = useSelector(plannedSamplesEnabledSelector);
  const unitDisabled = _notNil(sample?.sample_definition_api_id);
  const initialSample = initialiseSample(sampleType, sample);
  const formMethods = useForm<InitialSample>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: initialSample,
  });
  const { register, control, errors, handleSubmit } = formMethods;
  const { fields: details } = useFieldArray<SampleMeasurement>({
    control,
    name: 'details',
  });
  const metaFieldValues = sampleMetadataFields(sampleMetadataGlossary, initialSample);

  const {
    sendRequest: checkIfNameIsUnique,
    requestSending: checkingIfNameIsUnique,
    requestError: nameValidationRequestError,
  } = useRequest<{ value: unknown }, { is_unique: boolean }>({
    route: 'samples.validate.isNameUnique',
    method: 'post',
  });

  const {
    sendRequest: saveSample,
    requestSending: savingSample,
    requestError: saveSampleError,
  } = useRequest({
    route: _notNil(sample) ? 'animal.sample.update' : 'animal.sample.create',
    params: {
      id: sample?.id ?? animalId,
    },
    method: _notNil(sample) ? 'put' : 'post',
  });

  const validateNameIsUnique = async (value: string): Promise<true | string> => {
    if (sample?.sample_id !== value) {
      const response = await checkIfNameIsUnique({ value });
      return (response?.data?.is_unique ?? true) ? true : 'This name is already in use, please try another';
    } else {
      return true; // the sample name has already been reserved
    }
  };

  interface FormData {
    sample_id?: string;
    date?: string;
    timepoint?: string;
    meta: {
      glossary_id: number;
      title: string;
      value: string;
    };
    comments?: string;
    details?: Array<SampleMeasurement>;
    reason_for_change?: string;
  }

  interface FormattedPayload extends Pick<FormData, 'sample_id' | 'comments' | 'details'> {
    team_glossary_api_id: string;
    date?: string | null;
    metadata: unknown;
    timepoint?: string | null;
    reason_for_change?: string;
  }

  const handlePayload = (data: FormData): FormattedPayload => {
    const formattedData: FormattedPayload = {
      team_glossary_api_id: sampleType.team_glossary_api_id,
      details: data.details?.filter((detail) => !isNaN(Number(detail.value))) ?? [],
      metadata: mapMetadataSubmissions(data.meta, sampleMetadataGlossary),
      comments: data.comments,
      reason_for_change: data.reason_for_change,
    };

    if (_notNil(sample)) {
      // UPDATE: Keep empty string if user cleared the input
      formattedData.date = data.date ?? null;
    } else {
      // CREATE: Use provided date or fall back to collectedAt if undefined/null
      formattedData.date = data.date ?? collectedAt;
    }

    // Handle timepoint differently for create vs update
    if (_notNil(sample)) {
      // UPDATE: Always include timepoint (null clears it, string sets it)
      formattedData.timepoint = _isNotBlank(data.timepoint) ? data.timepoint : null;
    } else {
      // CREATE: Only include timepoint if it has a value (omit if empty)
      if (_isNotBlank(data.timepoint)) {
        formattedData.timepoint = data.timepoint;
      }
    }

    if (allowNaming && _notNil(data?.sample_id)) {
      formattedData.sample_id = data.sample_id;
    }

    return formattedData;
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    // Convert timepoint shorthand to ISO 8601
    if (_isNotBlank(data.timepoint)) {
      try {
        const duration = await parseDuration(data.timepoint);
        data.timepoint = durationSerialize(duration);
      } catch {
        // If conversion fails, validation should have caught it, but keep original value
      }
    }

    const payload = handlePayload(data);
    const response = await saveSample(payload);
    handleAfterSave(response);
  };

  const disableSavePress =
    checkingIfNameIsUnique || savingSample || (plannedSamplesEnabled && _notNil(errors.timepoint));

  return (
    <FormProvider {...formMethods}>
      <form
        ref={formContainerRef}
        onSubmit={handleSubmit(onSubmit)}
        className="bl b--moon-gray min-h-100"
        data-test-component="SampleForm"
        data-test-element="form-container"
      >
        <>
          {_notNil(sampleType?.title) && <h3 className="lh-title f5 fw5 ph3 pt3">{sampleType.title}</h3>}
          {nameValidationRequestError !== false || saveSampleError !== false ? (
            <ApiErrorBanner className="ma3" error={nameValidationRequestError ?? saveSampleError} />
          ) : null}
          {includeDatetimeField && (
            <div className="pa3">
              <label htmlFor="date">Date collected</label>
              <Controller
                name="date"
                control={control}
                rules={{
                  required: sample?.status === 'collected' ? 'This field is required' : false,
                }}
                render={({ value, onChange }: ControllerRenderProps) => (
                  <DateTimePickerNative value={value} onChange={onChange} />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="date"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
          )}
          {allowNaming && (
            <div className="pa3">
              <label htmlFor="sample_id">Unique sample name</label>
              <input
                autoFocus
                type="text"
                name="sample_id"
                ref={register({
                  required: 'This field is required',
                  minLength: {
                    value: 3,
                    message: 'Must be a minimum of 3 characters',
                  },
                  maxLength: {
                    value: 255,
                    message: 'Must be a maximum of 255 characters',
                  },
                  validate: {
                    validateNameIsUnique,
                  },
                })}
                style={{ marginBottom: 0 }}
              />
              <ErrorMessage
                errors={errors}
                name="sample_id"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
          )}
          {_isNotEmpty(details) && (
            <div
              className="w-100 pa2 flex justify-between flex-wrap"
              style={{ maxWidth: '32rem' }}
              data-test-element="details-container"
            >
              {details.map((detailProps: any, fieldIndex: any) => (
                <DetailField
                  key={detailProps.key}
                  detailKey={detailProps.key as string}
                  fieldIndex={fieldIndex}
                  value={detailProps.value}
                  unit={detailProps.unit}
                  unitDisabled={unitDisabled}
                />
              ))}
            </div>
          )}
          {plannedSamplesEnabled && (
            <div className="pa3">
              <Timepoint name="timepoint" label="Timepoint" allow_zero allow_negative />
            </div>
          )}
          <div className="pa3" data-test-element="comment-container">
            <label htmlFor="comments">Comment</label>
            <textarea
              name="comments"
              data-test-element="comment-textarea"
              style={{ marginBottom: 0 }}
              ref={register({
                maxLength: {
                  value: 255,
                  message: 'Must be a maximum of 255 characters',
                },
              })}
            />
          </div>
          {_isNotEmpty(metaFieldValues) && (
            <div className="ph3">
              <FormRender fields={metaFieldValues} />
            </div>
          )}
          {_notNil(sample) && (
            <div className="ph3 mb3">
              <ReasonForChange />
            </div>
          )}
          <div className="pa3 mb3" data-test-element="action-container">
            <Button submit disabled={disableSavePress}>
              {saveButtonText}
            </Button>
          </div>
        </>
      </form>
    </FormProvider>
  );
};

export default SampleForm;
